import { action, computed, makeObservable, observable } from 'mobx';
import { Store } from '../../store/store';
import { BindingProps, StoreNode } from '../../store';
import { JobModel, MomentSelector, Speaker, SubTopic, Topic } from '../../entities';
import React from 'react';

const GENERIC_SELECTOR_MIN_VISIBLE = 10;
const TOPIC_SELECTOR_MIN_VISIBLE = 10;

type Props = BindingProps<{
  jobId: string
  selector: MomentSelector
}>

export class MomentSplashState
  extends StoreNode {

  constructor(store: Store, props?: Props) {
    super(store, props);
    makeObservable(this, {
      jobId: computed,
      job: computed,
      selector: computed,
      isTopicListExpanded: observable,
      toggleTopicListExpansion: action,
      collapseTopicList: action,
      isTopicListExpandable: computed,
      topics: computed,
      selectedTopics: computed,
      filteredTopics: computed,
      visibleTopics: computed,
      handleTopicClick: action,
      isSubTopicListExpanded: observable,
      toggleSubTopicListExpansion: action,
      collapseSubTopicList: action,
      isSubTopicListExpandable: computed,
      subTopics: computed,
      selectedSubTopics: computed,
      filteredSubTopics: computed,
      visibleSubTopics: computed,
      handleSubTopicClick: action,
      isSpeakerListExpanded: observable,
      toggleSpeakerListExpansion: action,
      collapseSpeakerList: action,
      isSpeakerListExpandable: computed,
      speakers: computed,
      selectedSpeakers: computed,
      filteredSpeakers: computed,
      visibleSpeakers: computed,
      handleSpeakerClick: action,
      selectAll: action,
      unselectAll: action,
      collapseLists: action,
    });
  }

  get jobId(): string | null {
    return this.getResolvedProp('jobId') || null;
  }

  get job(): JobModel | null {
    return this.store.maybeGetJob(this.jobId);
  }

  get selector(): MomentSelector | null {
    return this.getResolvedProp('selector');
  }


  isTopicListExpanded: boolean = false;

  toggleTopicListExpansion() {
    this.isTopicListExpanded = !this.isTopicListExpanded;
  }
  collapseTopicList() {
    this.isTopicListExpanded = false;
  }


  get isTopicListExpandable() {
    const { selector } = this;
    if (!selector)
      return false;
      
    return this.topics.length > TOPIC_SELECTOR_MIN_VISIBLE;
  }

  get topics(): Topic[] {
    return this.selector?.topics || [];
  }
  get selectedTopics(): Topic[] {
    return this.selector?.selectedTopics || [];
  }
  get filteredTopics(): Topic[] {
    return this.selector?.filteredTopics || [];
  }
  get visibleTopics(): Topic[] {
    
    const topics = this.filteredTopics;
    const expandable = this.isTopicListExpandable;
    const expanded = this.isTopicListExpanded;
    const visibleCount = (expandable && !expanded) ? TOPIC_SELECTOR_MIN_VISIBLE : topics.length;

    return topics.slice(0, visibleCount);
  }

  handleTopicClick = (evt: React.MouseEvent, target: Topic) => {
    this.selector?.toggleTopic(target);
  }





  isSubTopicListExpanded: boolean = false;

  toggleSubTopicListExpansion() {
    this.isSubTopicListExpanded = !this.isSubTopicListExpanded;
  }
  collapseSubTopicList() {
    this.isSubTopicListExpanded = false;
  }

  get isSubTopicListExpandable() {
    const { selector } = this;
    if (!selector)
      return false;

    return selector.selectedSubTopics.length > GENERIC_SELECTOR_MIN_VISIBLE;
  }

  get subTopics(): SubTopic[] {
    return this.selector?.subTopics || [];
  }
  get selectedSubTopics(): SubTopic[] {
    return this.selector?.selectedSubTopics || [];
  }
  get filteredSubTopics(): SubTopic[] {
    return this.selector?.filteredSubTopics || [];
  }

  get visibleSubTopics(): SubTopic[] {
    
    const subTopics = this.filteredSubTopics;
    const expandable = this.isSubTopicListExpandable;
    const expanded = this.isSubTopicListExpanded;
    const visibleCount = (expandable && !expanded) ? GENERIC_SELECTOR_MIN_VISIBLE : subTopics.length;

    return subTopics.slice(0, visibleCount);
  }

  handleSubTopicClick = (evt: React.MouseEvent, target: SubTopic) => {
    this.selector?.toggleSubTopic(target);
  }

  


  
  isSpeakerListExpanded: boolean = false;

  toggleSpeakerListExpansion() {
    this.isSpeakerListExpanded = !this.isSpeakerListExpanded;
  }
  collapseSpeakerList() {
    this.isSpeakerListExpanded = false;
  }

  get isSpeakerListExpandable() {
    const { selector } = this;
    if (!selector)
      return false;

    return selector.selectedSpeakers.length > GENERIC_SELECTOR_MIN_VISIBLE;
  }

  get speakers(): Speaker[] {
    return this.selector?.speakers || [];
  }
  get selectedSpeakers(): Speaker[] {
    return this.selector?.selectedSpeakers || [];
  }
  get filteredSpeakers(): Speaker[] {
    return this.selector?.filteredSpeakers || [];
  }

  get visibleSpeakers(): Speaker[] {
    
    const speakers = this.filteredSpeakers;
    const expandable = this.isSpeakerListExpandable;
    const expanded = this.isSpeakerListExpanded;
    const visibleCount = (expandable && !expanded) ? GENERIC_SELECTOR_MIN_VISIBLE : speakers.length;

    return speakers.slice(0, visibleCount);
  }

  handleSpeakerClick = (evt: React.MouseEvent, target: Speaker) => {
    this.selector?.toggleSpeaker(target);
  }
  
  selectAll() {
    this.selector?.selectAll();
  }

  unselectAll() {
    this.selector?.unselectAll();
  }

  collapseLists() {
    this.collapseTopicList();
    this.collapseSubTopicList();
  }
}